// ImageCORSHandler.ts - Client-side only CORS solution
import { createSignal, createEffect } from "solid-js";
import { isVpnMode, playListUrl } from "../../state";

interface ImageLoadResult {
  success: boolean;
  url: string;
  method: "direct" | "base64" | "proxy" | "fallback";
  error?: string;
}

// Helper functions OUTSIDE the class
function checkBlobSupport(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.URL !== "undefined" &&
      typeof window.URL.createObjectURL === "function"
    );
  } catch (error) {
    return false;
  }
}

function base64ToBlobUrl(base64: string): string {
  if (!checkBlobSupport()) {
    console.log("📱 Older webOS TV detected, using data URL directly");
    return base64;
  }

  try {
    const parts = base64.split(",");
    const byteString = atob(parts[1]);
    const mimeString = parts[0].split(":")[1].split(";")[0];

    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([ab], { type: mimeString });
    const blobUrl = window.URL.createObjectURL(blob);
    console.log("🔄 Created blob URL for newer webOS TV");
    return blobUrl;
  } catch (error) {
    console.error("❌ Error creating blob URL, falling back to data URL:", error);
    return base64;
  }
}

class ImageCORSHandler {
  private cache = new Map<string, string>();
  private loadingPromises = new Map<string, Promise<ImageLoadResult>>();

  /**
   * Main method to load images with fallback strategies
   * @param originalUrl - The original image URL
   * @param fallbackUrl - Fallback image URL (optional)
   * @returns Promise<ImageLoadResult>
   */
  async loadImage(originalUrl: string, fallbackUrl?: string): Promise<ImageLoadResult> {
    if (!originalUrl) {
      return {
        success: false,
        url: fallbackUrl || "/assets/placeholder.png",
        method: "fallback",
        error: "No URL provided",
      };
    }

    // Check cache first
    if (this.cache.has(originalUrl)) {
      return {
        success: true,
        url: this.cache.get(originalUrl)!,
        method: "direct",
      };
    }

    // Check if already loading
    if (this.loadingPromises.has(originalUrl)) {
      return this.loadingPromises.get(originalUrl)!;
    }

    // Create loading promise
    const loadingPromise = this.tryLoadStrategies(originalUrl, fallbackUrl);
    this.loadingPromises.set(originalUrl, loadingPromise);

    try {
      const result = await loadingPromise;

      // Cache successful results
      if (result.success) {
        this.cache.set(originalUrl, result.url);
      }

      return result;
    } finally {
      // Cleanup loading promise
      this.loadingPromises.delete(originalUrl);
    }
  }

  /**
   * Try different loading strategies in sequence
   */
  private async tryLoadStrategies(originalUrl: string, fallbackUrl?: string): Promise<ImageLoadResult> {
    console.log("🖼️ Loading image:", originalUrl);

    // Special handling for known problematic domains
    if (originalUrl.includes("img5.steam.bz") || originalUrl.includes("steam.bz")) {
      console.log("🚨 Detected problematic steam.bz domain, using proxy + base64");

      // For steam.bz, go directly to proxy and convert to base64
      try {
        const base64Url = await this.loadThroughProxy(originalUrl);
        console.log("✅ Proxy + base64 conversion successful for steam.bz");
        return {
          success: true,
          url: base64Url,
          method: "proxy",
        };
      } catch (error) {
        console.log("❌ Proxy failed for steam.bz:", error);
      }

      // All strategies failed for steam.bz
      console.warn("⚠️ All loading strategies failed for steam.bz, using fallback");
      return {
        success: false,
        url: fallbackUrl || "/assets/placeholder.png",
        method: "fallback",
        error: "All loading strategies failed for steam.bz domain",
      };
    }

    // Special handling for other known problematic domains
    if (
      originalUrl.includes("selling-team.click") ||
      originalUrl.includes("oct24.") ||
      originalUrl.includes("sept2.")
    ) {
      console.log("🚨 Detected problematic domain, trying proxy + base64");

      try {
        const base64Url = await this.loadThroughProxy(originalUrl);
        console.log("✅ Proxy + base64 conversion successful for problematic domain");
        return {
          success: true,
          url: base64Url,
          method: "proxy",
        };
      } catch (error) {
        console.log("❌ Proxy failed for problematic domain:", error);

        // Fallback immediately for these domains
        console.warn("⚠️ Using fallback for problematic domain");
        return {
          success: false,
          url: fallbackUrl || "/assets/placeholder.png",
          method: "fallback",
          error: "All loading strategies failed for problematic domain",
        };
      }
    }

    // For other domains, try the normal sequence
    // Strategy 1: Direct load (fastest if it works)
    try {
      await this.tryDirectLoad(originalUrl);
      console.log("✅ Direct load successful");
      return {
        success: true,
        url: originalUrl,
        method: "direct",
      };
    } catch (error) {
      console.log("❌ Direct load failed:", error);
    }

    // Strategy 2: Convert to base64 (works for many CORS restricted images)
    try {
      const base64Url = await this.convertToBase64(originalUrl);
      console.log("✅ Base64 conversion successful");
      return {
        success: true,
        url: base64Url,
        method: "base64",
      };
    } catch (error) {
      console.log("❌ Base64 conversion failed:", error);
    }

    // Strategy 3: Use your proxy system
    try {
      const proxyUrl = await this.loadThroughProxy(originalUrl);
      console.log("✅ Proxy load successful");
      return {
        success: true,
        url: proxyUrl,
        method: "proxy",
      };
    } catch (error) {
      console.log("❌ Proxy load failed:", error);
    }

    // All strategies failed - return fallback
    console.warn("⚠️ All loading strategies failed, using fallback");
    return {
      success: false,
      url: fallbackUrl || "/assets/placeholder.png",
      method: "fallback",
      error: "All loading strategies failed",
    };
  }

  /**
   * Try to load image directly
   */
  private async tryDirectLoad(imageUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      const timeout = setTimeout(() => {
        reject(new Error("Direct load timeout"));
      }, 3000);

      img.onload = () => {
        clearTimeout(timeout);
        resolve();
      };

      img.onerror = () => {
        clearTimeout(timeout);
        reject(new Error("Direct load failed - network error"));
      };

      // Try with crossOrigin first
      img.crossOrigin = "anonymous";
      img.src = imageUrl;
    });
  }

  /**
   * Convert image to base64 data URI
   */
  private async convertToBase64(imageUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        try {
          // Create canvas and draw image
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          if (!ctx) {
            reject(new Error("Canvas context not available"));
            return;
          }

          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;

          ctx.drawImage(img, 0, 0);

          // Convert to base64 with good compression
          const dataURL = canvas.toDataURL("image/jpeg", 0.8);

          // Convert to blob URL for better webOS compatibility
          const finalUrl = base64ToBlobUrl(dataURL);
          console.log("🎯 Base64 result URL type:", finalUrl.startsWith("blob:") ? "blob" : "data");

          resolve(finalUrl);
        } catch (canvasError) {
          console.log("Canvas method failed, trying fetch...");
          // Try alternative fetch method
          this.fetchAsBase64(imageUrl).then(resolve).catch(reject);
        }
      };

      img.onerror = () => {
        // Try without crossOrigin
        const img2 = new Image();
        img2.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            if (!ctx) {
              reject(new Error("Canvas context not available"));
              return;
            }

            canvas.width = img2.naturalWidth || img2.width;
            canvas.height = img2.naturalHeight || img2.height;
            ctx.drawImage(img2, 0, 0);

            const dataURL = canvas.toDataURL("image/jpeg", 0.8);

            // Convert to blob URL for better webOS compatibility
            const finalUrl = base64ToBlobUrl(dataURL);
            console.log("🎯 No-CORS base64 result URL type:", finalUrl.startsWith("blob:") ? "blob" : "data");

            resolve(finalUrl);
          } catch (error) {
            reject(error);
          }
        };

        img2.onerror = () => reject(new Error("Base64 conversion failed"));
        img2.src = imageUrl; // Try without crossOrigin
      };

      img.crossOrigin = "anonymous";
      img.src = imageUrl;
    });
  }

  /**
   * Alternative base64 conversion using fetch
   */
  private async fetchAsBase64(imageUrl: string): Promise<string> {
    try {
      const response = await fetch(imageUrl, {
        mode: "no-cors",
      });

      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Result = reader.result as string;
          // Convert to blob URL for better webOS compatibility
          const finalUrl = base64ToBlobUrl(base64Result);
          resolve(finalUrl);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      throw new Error(`Fetch base64 failed: ${error.message}`);
    }
  }

  /**
   * Use your existing proxy system - MUST convert to base64 data URL
   */
  private async loadThroughProxy(imageUrl: string): Promise<string> {
    const proxyUrl = localStorage.getItem("proxy_url");

    if (!proxyUrl) {
      throw new Error("No proxy URL configured");
    }

    // Encode the image URL to base64 (following your pattern)
    const encodedUrl = btoa(imageUrl);
    const finalUrl = `${proxyUrl}${encodedUrl}`;

    console.log("🔄 Trying proxy fetch for:", imageUrl);
    console.log("🔗 Proxy URL:", finalUrl);

    try {
      // Try to fetch as blob and convert to base64 (this is crucial for Lightning TV)
      const response = await fetch(finalUrl, {
        method: "GET",
        mode: "cors",
        cache: "default",
        headers: {
          Accept: "image/*",
        },
      });

      if (!response.ok) {
        console.log(`❌ Proxy fetch failed with status: ${response.status}`);
        throw new Error(`Proxy fetch failed: ${response.status}`);
      }

      const blob = await response.blob();
      console.log("✅ Got blob from proxy, size:", blob.size, "type:", blob.type);
      console.log("🔄 Converting blob to base64...");

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Result = reader.result as string;
          console.log("✅ Base64 conversion complete! Length:", base64Result.length);
          console.log("🔍 Base64 preview:", base64Result.substring(0, 50) + "...");

          // Convert to blob URL for better webOS compatibility like your QR code
          const finalUrl = base64ToBlobUrl(base64Result);
          console.log("🎯 Final URL type:", finalUrl.startsWith("blob:") ? "blob" : "data");

          resolve(finalUrl);
        };
        reader.onerror = () => {
          console.log("❌ FileReader failed");
          reject(new Error("FileReader failed"));
        };
        reader.readAsDataURL(blob);
      });
    } catch (fetchError) {
      console.log("❌ Proxy fetch failed:", fetchError);

      // Try alternative approach - use img element to test if proxy URL works
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";

        const timeout = setTimeout(() => {
          reject(new Error("Proxy image load timeout"));
        }, 10000);

        img.onload = () => {
          clearTimeout(timeout);

          try {
            // Convert the loaded image to canvas and then to base64
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            if (!ctx) {
              reject(new Error("Canvas context not available"));
              return;
            }

            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            ctx.drawImage(img, 0, 0);

            const base64Result = canvas.toDataURL("image/jpeg", 0.8);
            console.log("✅ Proxy image loaded and converted to base64 via canvas");
            console.log("🔍 Canvas base64 preview:", base64Result.substring(0, 50) + "...");

            // Convert to blob URL for better webOS compatibility
            const finalUrl = base64ToBlobUrl(base64Result);
            console.log("🎯 Canvas result URL type:", finalUrl.startsWith("blob:") ? "blob" : "data");

            resolve(finalUrl);
          } catch (canvasError) {
            console.log("❌ Canvas conversion failed:", canvasError);
            reject(canvasError);
          }
        };

        img.onerror = () => {
          clearTimeout(timeout);
          console.log("❌ Proxy image load failed completely");
          reject(new Error("Proxy image load failed"));
        };

        console.log("🔄 Trying to load proxy URL via image element...");
        img.src = finalUrl;
      });
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
    console.log("Image cache cleared");
  }

  /**
   * Get cache stats
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// Singleton instance
const imageHandler = new ImageCORSHandler();

// Solid.js hook for easy integration
export function createCORSSafeImage() {
  const [imageUrl, setImageUrl] = createSignal<string>("");
  const [isLoading, setIsLoading] = createSignal(false);
  const [loadMethod, setLoadMethod] = createSignal<string>("");
  const [hasError, setHasError] = createSignal(false);

  const loadImage = async (originalUrl: string, fallbackUrl?: string) => {
    if (!originalUrl) return;

    setIsLoading(true);
    setHasError(false);

    try {
      const result = await imageHandler.loadImage(originalUrl, fallbackUrl);

      setImageUrl(result.url);
      setLoadMethod(result.method);
      setHasError(!result.success);

      console.log(`Image loaded via ${result.method}:`, result.url.substring(0, 50) + "...");
    } catch (error) {
      console.error("Image loading failed completely:", error);
      setImageUrl(fallbackUrl || "/assets/placeholder.png");
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    imageUrl,
    isLoading,
    loadMethod,
    hasError,
    loadImage,
    clearCache: () => imageHandler.clearCache(),
    getCacheStats: () => imageHandler.getCacheStats(),
  };
}

// Direct function for simple use cases
export async function getCORSSafeImageUrl(originalUrl: string, fallbackUrl?: string): Promise<string> {
  try {
    const result = await imageHandler.loadImage(originalUrl, fallbackUrl);
    return result.url;
  } catch (error) {
    console.error("Failed to get CORS-safe image URL:", error);
    return fallbackUrl || "/assets/placeholder.png";
  }
}

// Export the handler instance for advanced usage
export { imageHandler };

export default imageHandler;
